import { TextButton } from '@creditornot/cb-button';
import { DatePickerInput } from '@creditornot/cb-date-picker';
import { InputContainer } from '@creditornot/cb-form';
import { Plus, Trashbin } from '@creditornot/cb-icons';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Radio } from '@creditornot/cb-radio';
import { Select } from '@creditornot/cb-select';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { updateSubmissionData } from 'apis/legitimizer/kyb/api';
import { KybSubmissionData } from 'apis/legitimizer/types';
import { RFFFieldErrorWrapper } from 'common/components';
import { Typography as T } from 'common/typography';

import PEPInfo from './PEPInfo';
import { SectionForm } from './SectionForm';
import { getBeneficiariesDetailsSchema, getPepsSchema } from './validation';
import { KybProcessContext } from '../KybProcessContext';
import MultiInputFormRow from '../MultiInputFormRow';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { ALL_COUNTRIES } from '../countryCodes';
import {
  formatDate,
  transformBeneficiariesFormValuesToApi,
  transformKybSubmissionToBeneficiariesFormValues,
} from '../transformers';
import { BeneficiariesFormValues, BeneficiaryDetails, PEPDetails } from '../types';
import { useBeneficiaryDetailsFieldsConfigMemo, usePEPDetailsFieldsConfigMemo } from '../useFieldsConfigMemo';
import { YEAR_OF_EXPIRATION_RANGE, YEAR_OF_BIRTH_RANGE } from '../yearRanges';

type Props = {
  feedback?: string;
  submissionData: KybSubmissionData;
  onSubmitSuccess: (section: string, skipSection?: boolean) => Promise<void>;
};

const StyledHeading = styled(T.Heading6)`
  padding: 24px 0;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  width: 100%;

  & ~ p {
    margin-block-start: 8px;
  }
`;

const InfoTextWrapper = styled.div`
  margin-block-start: 16px;
  margin-block-end: 16px;
`;

const BusinessOwnersWrapper = styled.div`
  margin-block-start: 16px;
  margin-block-end: 24px;
`;

const Spacing = styled.div`
  margin-block-start: 32px;
  margin-block-end: 16px;
`;

const RadioGroup = styled.div`
  margin-block-end: 32px;
  display: flex;
  flex-direction: column;
`;

const RadioCard = styled.div<{ checked: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding: 12px;
  border: 1px solid;
  border-radius: 8px;

  & + & {
    margin-block-start: 12px;
  }

  border-color: ${({ checked }) => (checked ? colors.wolt100 : cssThemedValue('borderPrimary'))};
`;

const RadioInputWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRadio = styled(Radio)``;

const StyledLabel = styled.label`
  margin-inline-start: 8px;
`;

const InputDescription = styled(T.Small)`
  color: ${cssThemedValue('textSecondary')};
  margin-inline-start: 24px;
  margin-block-start: 4px;
`;

const StyledMultiInputRow = styled(MultiInputFormRow)`
  & + & {
    margin-block-start: 32px;
  }
`;

const FieldsWrapper = styled.div``;

const Fields = styled.div`
  padding-block-end: 26px;
`;

const StyledInputContainer = styled(InputContainer)`
  margin-block-end: 8px;
`;

const InputRowWrapper = styled.div`
  display: flex;
  margin-block-start: 10px;
`;

const InputWideWrapper = styled.div`
  flex: 0 0 232px;
`;

const InputNarrowWrapper = styled.div`
  flex: 0 0 152px;
`;

const InputExtendWrapper = styled.div`
  flex: 1 1 auto;
  margin-inline-start: 8px;
`;

const AddButton = styled(TextButton).attrs({ variant: 'blue' })`
  margin-block-start: 16px;
  margin-block-end: 16px;
`;

const RemoveButtonWrapper = styled.div`
  text-align: end;
  margin-block-end: 24px;
`;

const RemoveButton = styled(TextButton).attrs({ variant: 'red' })`
  padding: 2px;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
`;

const RowDivider = styled(Divider)`
  margin-block-end: 16px;
`;

const Beneficiaries = ({ feedback, submissionData, onSubmitSuccess }: Props) => {
  const intl = useIntl();

  const [hasPeps, setHasPeps] = useState(false);

  const initialFormValues = useMemo(() => {
    setHasPeps((submissionData.beneficiaries?.pepDetails?.length ?? 0) > 0);
    return transformKybSubmissionToBeneficiariesFormValues(submissionData.beneficiaries);
  }, [submissionData]);

  const sectionConfiguration = useContext(SectionConfigurationContext);
  const { processId } = useContext(KybProcessContext);

  const beneficiaryDetailsFieldsConfig = useBeneficiaryDetailsFieldsConfigMemo({ sectionConfiguration });
  const pepDetailsFieldsConfig = usePEPDetailsFieldsConfigMemo({ sectionConfiguration });

  const beneficiariesFormSchema = useMemo(() => {
    let schema = yup.object({});
    schema = schema.concat(getBeneficiariesDetailsSchema(false, beneficiaryDetailsFieldsConfig));
    if (hasPeps) {
      schema = schema.concat(getPepsSchema(false, pepDetailsFieldsConfig));
    }
    return schema;
  }, [beneficiaryDetailsFieldsConfig, pepDetailsFieldsConfig, hasPeps]);

  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const handleSubmit = async (data: BeneficiariesFormValues) => {
    const { uboDetails, pepDetails } = transformBeneficiariesFormValuesToApi(data);
    const submitData = {
      ...submissionData,
      beneficiaries: {
        uboDetails,
        pepDetails: hasPeps ? pepDetails : [],
      },
    };
    await updateSubmissionData(processId, submitData);
    await onSubmitSuccess(data.nextSection ? data.nextSection : 'beneficiaries', !!data.nextSection);
  };

  const allCountries = ALL_COUNTRIES.map(country => ({
    label: country.value,
    value: country.id,
  }));

  const isBeneficiariesRequired =
    beneficiaryDetailsFieldsConfig.city.required ||
    beneficiaryDetailsFieldsConfig.country.required ||
    beneficiaryDetailsFieldsConfig.dateOfBirth.required ||
    beneficiaryDetailsFieldsConfig.name.required ||
    beneficiaryDetailsFieldsConfig.nationalId.required ||
    beneficiaryDetailsFieldsConfig.nationality.required ||
    beneficiaryDetailsFieldsConfig.postalCode.required ||
    beneficiaryDetailsFieldsConfig.street.required ||
    beneficiaryDetailsFieldsConfig.yearOfBirth.required;

  const isPEPRequired =
    pepDetailsFieldsConfig.country.required ||
    pepDetailsFieldsConfig.dateOfExpiration.required ||
    pepDetailsFieldsConfig.name.required ||
    pepDetailsFieldsConfig.officialPosition.required ||
    pepDetailsFieldsConfig.yearOfExpiration.required;

  return (
    <SectionForm
      subtitle={t('compliance.kyb.sections.beneficiaries.info')}
      initialFormValues={initialFormValues}
      schema={beneficiariesFormSchema}
      feedback={feedback}
      onSubmit={handleSubmit}
    >
      {({ submitting, values }) => (
        <>
          <StyledHeading>
            <FormattedMessage id="compliance.kyb.sections.beneficiaries.business-owners.heading" />
          </StyledHeading>
          <BusinessOwnersWrapper>
            <T.Body2>
              <FormattedMessage
                id="compliance.kyb.sections.beneficiaries.business-owners.info"
                values={{
                  uboPercentage: 25,
                }}
              />
            </T.Body2>
          </BusinessOwnersWrapper>

          <StyledMultiInputRow
            required={isBeneficiariesRequired}
            legendText={t('compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.label')}
          >
            <FieldArray<BeneficiaryDetails> name="beneficiaries">
              {({ fields }) => (
                <>
                  {fields.map((name: string, index: number) => (
                    <FieldsWrapper key={name}>
                      <Fields>
                        {beneficiaryDetailsFieldsConfig.name.visible && (
                          <Field<BeneficiaryDetails['name']> name={`${name}.name`} key={`${name}.name`}>
                            {field => (
                              <StyledInputContainer
                                label={t(
                                  'compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.name',
                                )}
                                labelHtmlFor={`${name}.name`}
                              >
                                <RFFFieldErrorWrapper field={field} key={`${name}.name`}>
                                  {input => <Input {...input} id={`${name}.name`} disabled={submitting} />}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}
                        {beneficiaryDetailsFieldsConfig.street.visible && (
                          <Field<BeneficiaryDetails['street']> name={`${name}.street`} key={`${name}.street`}>
                            {field => (
                              <StyledInputContainer
                                label={t(
                                  'compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.street',
                                )}
                                labelHtmlFor={`${name}.street`}
                              >
                                <RFFFieldErrorWrapper field={field} key={`${name}.street`}>
                                  {input => <Input {...input} id={`${name}.street`} disabled={submitting} />}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}

                        <InputRowWrapper>
                          {beneficiaryDetailsFieldsConfig.postalCode.visible && (
                            <InputNarrowWrapper>
                              <Field<BeneficiaryDetails['postalCode']>
                                name={`${name}.postalCode`}
                                key={`${name}.postalCode`}
                              >
                                {field => (
                                  <StyledInputContainer
                                    label={t(
                                      'compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.postal-code',
                                    )}
                                    labelHtmlFor={`${name}.postalCode`}
                                  >
                                    <RFFFieldErrorWrapper field={field}>
                                      {input => <Input {...input} id={`${name}.postalCode`} disabled={submitting} />}
                                    </RFFFieldErrorWrapper>
                                  </StyledInputContainer>
                                )}
                              </Field>
                            </InputNarrowWrapper>
                          )}
                          {beneficiaryDetailsFieldsConfig.city.visible && (
                            <InputExtendWrapper>
                              <Field<BeneficiaryDetails['city']> name={`${name}.city`} key={`${name}.city`}>
                                {field => (
                                  <StyledInputContainer
                                    label={t(
                                      'compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.city',
                                    )}
                                    labelHtmlFor={`${name}.city`}
                                  >
                                    <RFFFieldErrorWrapper field={field}>
                                      {input => <Input {...input} id={`${name}.city`} disabled={submitting} />}
                                    </RFFFieldErrorWrapper>
                                  </StyledInputContainer>
                                )}
                              </Field>
                            </InputExtendWrapper>
                          )}
                        </InputRowWrapper>
                        {beneficiaryDetailsFieldsConfig.country.visible && (
                          <Field<BeneficiaryDetails['country']> name={`${name}.country`} key={`${name}.country`}>
                            {field => (
                              <StyledInputContainer
                                label={t('compliance.kyb.address.country')}
                                labelHtmlFor={`${name}.country`}
                              >
                                <RFFFieldErrorWrapper field={field}>
                                  {input => (
                                    <Select
                                      inputId={`${name}.country`}
                                      options={allCountries}
                                      disabled={submitting}
                                      {...input}
                                    />
                                  )}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}
                        {beneficiaryDetailsFieldsConfig.nationality.visible && (
                          <Field<BeneficiaryDetails['nationality']>
                            name={`${name}.nationality`}
                            key={`${name}.nationality`}
                          >
                            {field => (
                              <StyledInputContainer
                                label={t('compliance.kyb.person-info.nationality')}
                                labelHtmlFor={`${name}.nationality`}
                              >
                                <RFFFieldErrorWrapper field={field}>
                                  {input => (
                                    <Select
                                      inputId={`${name}.nationality`}
                                      options={allCountries}
                                      disabled={submitting}
                                      {...input}
                                    />
                                  )}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}
                        {beneficiaryDetailsFieldsConfig.nationalId.visible && (
                          <Field<BeneficiaryDetails['nationalId']>
                            name={`${name}.nationalId`}
                            key={`${name}.nationalId`}
                          >
                            {field => (
                              <StyledInputContainer
                                label={t('compliance.kyb.sections.basic-info.business-information.national-id')}
                                labelHtmlFor={`${name}.nationalId`}
                              >
                                <RFFFieldErrorWrapper field={field} key={`${name}.nationalId`}>
                                  {input => <Input {...input} id={`${name}.nationalId`} disabled={submitting} />}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}
                        <InputRowWrapper>
                          {beneficiaryDetailsFieldsConfig.dateOfBirth.visible && (
                            <InputWideWrapper>
                              <Field<BeneficiaryDetails['dateOfBirth']>
                                name={`${name}.dateOfBirth`}
                                key={`${name}.dateOfBirth`}
                              >
                                {field => (
                                  <StyledInputContainer
                                    label={t(
                                      'compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.date-of-birth',
                                    )}
                                    labelHtmlFor={`${name}.dateOfBirth`}
                                  >
                                    <RFFFieldErrorWrapper field={field}>
                                      {input => (
                                        <DatePickerInput
                                          {...input}
                                          inputId={`${name}.dateOfBirth`}
                                          closeOnChange
                                          formatValue={formatDate}
                                          disableYearSelect
                                          popoverZIndex={100005}
                                          disabled={submitting}
                                        />
                                      )}
                                    </RFFFieldErrorWrapper>
                                  </StyledInputContainer>
                                )}
                              </Field>
                            </InputWideWrapper>
                          )}
                          {beneficiaryDetailsFieldsConfig.yearOfBirth.visible && (
                            <InputExtendWrapper>
                              <Field<BeneficiaryDetails['yearOfBirth']>
                                name={`${name}.yearOfBirth`}
                                key={`${name}.yearOfBirth`}
                              >
                                {field => (
                                  <StyledInputContainer
                                    label={t(
                                      'compliance.kyb.sections.beneficiaries.business-owners.business-owner-details.year-of-birth',
                                    )}
                                    labelHtmlFor={`${name}.yearOfBirth`}
                                  >
                                    <RFFFieldErrorWrapper field={field}>
                                      {input => (
                                        <Select
                                          {...input}
                                          options={YEAR_OF_BIRTH_RANGE}
                                          inputId={`${name}.yearOfBirth`}
                                          disabled={submitting}
                                        />
                                      )}
                                    </RFFFieldErrorWrapper>
                                  </StyledInputContainer>
                                )}
                              </Field>
                            </InputExtendWrapper>
                          )}
                        </InputRowWrapper>
                      </Fields>
                      {index !== 0 && (
                        <RemoveButtonWrapper>
                          <RemoveButton
                            icon={<Trashbin />}
                            onClick={() => {
                              fields.remove(index);
                            }}
                          >
                            <FormattedMessage id="compliance.kyb.sections.beneficiaries.business-owners.remove" />
                          </RemoveButton>
                        </RemoveButtonWrapper>
                      )}
                      <RowDivider />
                    </FieldsWrapper>
                  ))}
                  <AddButton onClick={() => fields.push({})} icon={<Plus />}>
                    <FormattedMessage id="compliance.kyb.sections.beneficiaries.business-owners.add" />
                  </AddButton>
                </>
              )}
            </FieldArray>
          </StyledMultiInputRow>

          <StyledHeading>
            <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.heading" />
          </StyledHeading>

          <InfoTextWrapper>
            <T.Body2>
              <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.info1" />
            </T.Body2>
          </InfoTextWrapper>
          <InfoTextWrapper>
            <T.Body2>
              <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.info2" />
            </T.Body2>
          </InfoTextWrapper>
          <PEPInfo />

          <StyledMultiInputRow
            required
            legendText={t('compliance.kyb.sections.beneficiaries.politically-exposed-persons.has-peps.label')}
          >
            <RadioGroup>
              <RadioCard
                checked={!hasPeps}
                onClick={() => {
                  setHasPeps(false);
                }}
              >
                <RadioInputWrapper>
                  <StyledRadio
                    aria-describedby="describe-no"
                    name="hasPEPsNo"
                    inputProps={{
                      id: 'hasPEPsNo',
                    }}
                    checked={!hasPeps}
                    readOnly
                  />
                  <StyledLabel htmlFor="hasPEPsNo">
                    <T.Body3>
                      <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.has-peps.no" />
                    </T.Body3>
                  </StyledLabel>
                </RadioInputWrapper>
                <InputDescription id="describe-no">
                  <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.has-peps.label-no" />
                </InputDescription>
              </RadioCard>

              <RadioCard
                checked={hasPeps}
                onClick={() => {
                  setHasPeps(true);
                  if (values.peps.length === 0) {
                    values.peps.push({ name: undefined });
                  }
                }}
              >
                <RadioInputWrapper>
                  <StyledRadio
                    aria-describedby="describe-yes"
                    name="hasPEPsYes"
                    inputProps={{
                      id: 'hasPEPsYes',
                    }}
                    checked={hasPeps}
                    readOnly
                  />
                  <StyledLabel htmlFor="hasPEPsYes">
                    <T.Body3>
                      <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.has-peps.yes" />
                    </T.Body3>
                  </StyledLabel>
                </RadioInputWrapper>
                <InputDescription id="describe-yes">
                  <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.has-peps.label-yes" />
                </InputDescription>
              </RadioCard>
            </RadioGroup>
          </StyledMultiInputRow>

          {hasPeps && (
            <>
              <Spacing>
                <T.Body2>
                  <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.info" />
                </T.Body2>
              </Spacing>
              <StyledMultiInputRow
                required={isPEPRequired}
                legendText={t('compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.label')}
              >
                <FieldArray<PEPDetails> name="peps">
                  {({ fields }) => (
                    <>
                      {fields.map((name: string, index: number) => (
                        <FieldsWrapper key={name}>
                          <Fields>
                            {pepDetailsFieldsConfig.name.visible && (
                              <Field<PEPDetails['name']> name={`${name}.name`} key={`${name}.name`}>
                                {field => (
                                  <StyledInputContainer
                                    label={t(
                                      'compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.name',
                                    )}
                                    labelHtmlFor={`${name}.name`}
                                  >
                                    <RFFFieldErrorWrapper field={field}>
                                      {input => <Input {...input} id={`${name}.name`} disabled={submitting} />}
                                    </RFFFieldErrorWrapper>
                                  </StyledInputContainer>
                                )}
                              </Field>
                            )}

                            {pepDetailsFieldsConfig.officialPosition.visible && (
                              <Field<PEPDetails['officialPosition']>
                                name={`${name}.officialPosition`}
                                key={`${name}.officialPosition`}
                              >
                                {field => (
                                  <StyledInputContainer
                                    label={t(
                                      'compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.official-position',
                                    )}
                                    labelHtmlFor={`${name}.officialPosition`}
                                  >
                                    <RFFFieldErrorWrapper field={field}>
                                      {input => (
                                        <Input {...input} id={`${name}.officialPosition`} disabled={submitting} />
                                      )}
                                    </RFFFieldErrorWrapper>
                                  </StyledInputContainer>
                                )}
                              </Field>
                            )}

                            {pepDetailsFieldsConfig.country.visible && (
                              <Field<PEPDetails['country']> name={`${name}.country`} key={`${name}.country`}>
                                {field => (
                                  <StyledInputContainer
                                    label={t(
                                      'compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.country',
                                    )}
                                    labelHtmlFor={`${name}.country`}
                                  >
                                    <RFFFieldErrorWrapper field={field}>
                                      {input => (
                                        <Select
                                          inputId={`${name}.country`}
                                          options={allCountries}
                                          disabled={submitting}
                                          {...input}
                                        />
                                      )}
                                    </RFFFieldErrorWrapper>
                                  </StyledInputContainer>
                                )}
                              </Field>
                            )}

                            {pepDetailsFieldsConfig.dateOfExpiration.visible && (
                              <InputRowWrapper>
                                <InputWideWrapper>
                                  <Field<PEPDetails['dateOfExpiration']>
                                    name={`${name}.dateOfExpiration`}
                                    key={`${name}.dateOfExpiration`}
                                  >
                                    {field => (
                                      <StyledInputContainer
                                        label={t(
                                          'compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.date-of-expiration',
                                        )}
                                        labelHtmlFor={`${name}.dateOfExpiration`}
                                      >
                                        <RFFFieldErrorWrapper field={field}>
                                          {input => (
                                            <DatePickerInput
                                              {...input}
                                              inputId={`${name}.dateOfExpiration`}
                                              closeOnChange
                                              formatValue={formatDate}
                                              disableYearSelect
                                              popoverZIndex={100005}
                                              disabled={submitting}
                                            />
                                          )}
                                        </RFFFieldErrorWrapper>
                                      </StyledInputContainer>
                                    )}
                                  </Field>
                                </InputWideWrapper>
                                <InputExtendWrapper>
                                  <Field<PEPDetails['yearOfExpiration']>
                                    name={`${name}.yearOfExpiration`}
                                    key={`${name}.yearOfExpiration`}
                                  >
                                    {field => (
                                      <StyledInputContainer
                                        label={t(
                                          'compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.year-of-expiration',
                                        )}
                                        labelHtmlFor={`${name}.yearOfExpiration`}
                                      >
                                        <RFFFieldErrorWrapper field={field}>
                                          {input => (
                                            <Select
                                              {...input}
                                              options={YEAR_OF_EXPIRATION_RANGE}
                                              inputId={`${name}.yearOfExpiration`}
                                              disabled={submitting}
                                            />
                                          )}
                                        </RFFFieldErrorWrapper>
                                      </StyledInputContainer>
                                    )}
                                  </Field>
                                </InputExtendWrapper>
                              </InputRowWrapper>
                            )}
                          </Fields>
                          <RemoveButtonWrapper>
                            <RemoveButton
                              icon={<Trashbin />}
                              onClick={() => {
                                fields.remove(index);
                                setHasPeps((fields?.length ?? 0) > 1);
                              }}
                            >
                              <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.remove" />
                            </RemoveButton>
                          </RemoveButtonWrapper>
                          <RowDivider />
                        </FieldsWrapper>
                      ))}
                      {hasPeps && (
                        <AddButton onClick={() => fields.push({})} icon={<Plus />}>
                          <FormattedMessage id="compliance.kyb.sections.beneficiaries.politically-exposed-persons.pep-details.add" />
                        </AddButton>
                      )}
                    </>
                  )}
                </FieldArray>
              </StyledMultiInputRow>
            </>
          )}
        </>
      )}
    </SectionForm>
  );
};

export default Beneficiaries;
